import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ClockIcon, 
  CheckCircleIcon, 
  ExclamationTriangleIcon,
  CalendarIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { TaskService, formatTaskFromApi } from '../../services/TaskService';
import { ProjectService } from '../../services/ProjectService';
import EditTaskModal from '../tasks/EditTaskModal';
import { getStatusLabel, getStatusColorClasses } from '../../utils/statusConfig';
import toast from 'react-hot-toast';

const BacklogView = () => {
  const [allTasks, setAllTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterProject, setFilterProject] = useState('all');

  useEffect(() => {
    loadAllTasks();
  }, []);

  const loadAllTasks = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Cargar todos los proyectos
      const projectsResult = await ProjectService.getProjects();
      if (!projectsResult.success) {
        throw new Error(projectsResult.error);
      }
      
      setProjects(projectsResult.projects || []);
      
      // Cargar tareas de todos los proyectos
      const tasksPromises = projectsResult.projects.map(project => 
        TaskService.getProjectTasks(project.id)
      );
      
      const tasksResults = await Promise.all(tasksPromises);
      const allTasksData = [];
      
      tasksResults.forEach((result, index) => {
        if (result.success && result.tasks) {
          const projectId = projectsResult.projects[index].id;
          const projectName = projectsResult.projects[index].name;
          const formattedTasks = result.tasks.map(task => ({
            ...formatTaskFromApi(task),
            projectId,
            projectName
          }));
          allTasksData.push(...formattedTasks);
        }
      });
      
      setAllTasks(allTasksData);
    } catch (err) {
      setError(err.message);
      toast.error('Error al cargar las tareas');
    } finally {
      setLoading(false);
    }
  };

  const handleTaskRowClick = (task) => {
    setSelectedTask(task);
    setShowEditModal(true);
  };

  const handleTaskUpdated = () => {
    loadAllTasks();
  };

  const handleDeleteTask = async (taskId, taskName, e) => {
    e.stopPropagation();
    
    const confirmed = window.confirm(`¿Estás seguro de que deseas eliminar la tarea "${taskName}"?`);
    if (!confirmed) return;

    const loadingToast = toast.loading('Eliminando tarea...');
    
    try {
      const result = await TaskService.deleteTask(taskId);
      
      if (result.success) {
        toast.success('Tarea eliminada exitosamente', { id: loadingToast });
        loadAllTasks();
      } else {
        toast.error(result.error || 'Error al eliminar la tarea', { id: loadingToast });
      }
    } catch (err) {
      toast.error('Error al eliminar la tarea', { id: loadingToast });
      console.error('Error deleting task:', err);
    }
  };

  // Filtrar tareas
  const filteredTasks = allTasks.filter(task => {
    const matchesSearch = task.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         task.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         task.projectName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || task.status === filterStatus;
    const matchesProject = filterProject === 'all' || task.projectId.toString() === filterProject;
    
    return matchesSearch && matchesStatus && matchesProject;
  });

  // Estadísticas
  const taskStats = {
    total: allTasks.length,
    pending: allTasks.filter(t => t.status === 'pending').length,
    inProgress: allTasks.filter(t => t.status === 'in progress').length,
    completed: allTasks.filter(t => t.status === 'completed').length,
    cancelled: allTasks.filter(t => t.status === 'cancelled').length
  };

  // Obtener icono según el estado
  const getStatusIcon = (status) => {
    const iconMap = {
      'pending': ClockIcon,
      'backlog': ClockIcon,
      'progress': ExclamationTriangleIcon,
      'in progress': ExclamationTriangleIcon,
      'completed': CheckCircleIcon,
      'cancelled': ExclamationTriangleIcon,
      'blocked': ExclamationTriangleIcon
    };
    return iconMap[status] || ClockIcon;
  };

  // Formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'Sin fecha';
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Calcular días restantes
  const getDaysRemaining = (finalDate) => {
    if (!finalDate) return null;
    const today = new Date();
    const endDate = new Date(finalDate);
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Backlog Refinado
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Gestión del backlog con estimaciones, priorización y refinamiento continuo
          </p>
        </div>
        <button
          onClick={loadAllTasks}
          className="btn-secondary flex items-center space-x-2"
          disabled={loading}
        >
          <DocumentTextIcon className="h-4 w-4" />
          <span>{loading ? 'Cargando...' : 'Actualizar'}</span>
        </button>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{taskStats.total}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Total</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-yellow-600">{taskStats.pending}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Pendientes</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-green-600">{taskStats.inProgress}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">En Progreso</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">{taskStats.completed}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Completadas</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-red-600">{taskStats.cancelled}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Canceladas</div>
        </div>
      </div>

      {/* Filtros */}
      <div className="card p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Búsqueda */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar tareas por nombre, descripción o proyecto..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field w-full pl-10"
              />
            </div>
          </div>
          
          {/* Filtro por estado */}
          <div className="md:w-48">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input-field w-full"
            >
              <option value="all">Todos los estados</option>
              <option value="pending">Pendientes</option>
              <option value="in progress">En Progreso</option>
              <option value="completed">Completadas</option>
              <option value="cancelled">Canceladas</option>
            </select>
          </div>
          
          {/* Filtro por proyecto */}
          <div className="md:w-48">
            <select
              value={filterProject}
              onChange={(e) => setFilterProject(e.target.value)}
              className="input-field w-full"
            >
              <option value="all">Todos los proyectos</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Lista de tareas */}
      <div className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 dark:text-red-400">{error}</p>
            <button 
              onClick={loadAllTasks}
              className="btn-primary mt-4"
            >
              Reintentar
            </button>
          </div>
        ) : filteredTasks.length > 0 ? (
          filteredTasks.map((task, index) => {
            const StatusIcon = getStatusIcon(task.status);
            const daysRemaining = getDaysRemaining(task.finalDate);
            
            return (
              <motion.div
                key={`${task.projectId}-${task.id}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="card p-4 hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-all relative cursor-pointer group"
                onClick={() => handleTaskRowClick(task)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getStatusColorClasses(task.status)}`}>
                      <StatusIcon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-semibold text-gray-900 dark:text-white">
                          {task.name}
                        </h3>
                        <PencilIcon className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {task.description}
                      </p>
                      <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                        <div className="flex items-center space-x-1">
                          <span className="font-medium text-blue-600 dark:text-blue-400">
                            📁 {task.projectName}
                          </span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <CalendarIcon className="h-3 w-3" />
                          <span>Inicio: {formatDate(task.initialDate)}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <CalendarIcon className="h-3 w-3" />
                          <span>Fin: {formatDate(task.finalDate)}</span>
                        </div>
                        {daysRemaining !== null && (
                          <div className={`flex items-center space-x-1 ${
                            daysRemaining < 0 ? 'text-red-500' : 
                            daysRemaining < 3 ? 'text-yellow-500' : 
                            'text-green-500'
                          }`}>
                            <ClockIcon className="h-3 w-3" />
                            <span>
                              {daysRemaining < 0 ? `${Math.abs(daysRemaining)} días vencido` :
                               daysRemaining === 0 ? 'Vence hoy' :
                               `${daysRemaining} días restantes`}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex flex-col items-end space-y-1">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColorClasses(task.status)}`}>
                        {getStatusLabel(task.status)}
                      </span>
                      <span className="text-xs text-gray-500">
                        ID: {task.id}
                      </span>
                    </div>
                    {/* Botones de acción */}
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={(e) => handleDeleteTask(task.id, task.name, e)}
                        className="p-1.5 rounded-lg bg-white dark:bg-gray-700 hover:bg-red-100 dark:hover:bg-red-600 transition-colors shadow-sm"
                        title="Eliminar tarea"
                      >
                        <TrashIcon className="h-3.5 w-3.5 text-red-600 dark:text-red-300" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })
        ) : (
          <div className="text-center py-12">
            <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">
              {searchTerm || filterStatus !== 'all' || filterProject !== 'all' 
                ? 'No se encontraron tareas con los filtros aplicados' 
                : 'No hay tareas disponibles'}
            </p>
          </div>
        )}
      </div>

      {/* Modal de Editar Tarea */}
      <EditTaskModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        task={selectedTask}
        onTaskUpdated={handleTaskUpdated}
      />
    </div>
  );
};

export default BacklogView;

